import { PrescricaoSituation, PrescricaoType } from '_metronic/helpers/enums'
import type { PrescricaoList, PrescricaoView } from '../_model'
import { TIPO_EXAME_GERAL, TipoDocumento } from './documentos'
import { ItemCatalogo, ItemPrescricao, criarItem, rotuloCatalogo } from './itens'

/**
 * Situação de uma prescrição assinada, derivada das datas (o backend só guarda ASSINADA):
 * dispensada na farmácia vira "utilizada" (só controle especial e retenção registram isso) e
 * a que passou da validade vira "vencida".
 */
export type SituacaoHistorico = 'assinada' | 'utilizada' | 'vencida'

/** Hoje como `AAAA-MM-DD` no fuso do navegador, para comparar com `LocalDate` sem `new Date`. */
const hojeLocal = (agora: Date) =>
  [
    agora.getFullYear(),
    String(agora.getMonth() + 1).padStart(2, '0'),
    String(agora.getDate()).padStart(2, '0'),
  ].join('-')

export const situacaoHistorico = (
  prescricao: Pick<PrescricaoList, 'dataUtilizacao' | 'dataValidade'>,
  agora = new Date()
): SituacaoHistorico => {
  if (prescricao.dataUtilizacao) return 'utilizada'
  if (prescricao.dataValidade && prescricao.dataValidade.slice(0, 10) < hojeLocal(agora)) {
    return 'vencida'
  }
  return 'assinada'
}

/** Tipo de documento e complemento (medicamento controlado ou tipo de exame) de uma prescrição salva. */
export const documentoDaPrescricao = (
  prescricao: Pick<PrescricaoView, 'tipo' | 'medicamentos' | 'exames'>
): { tipo: TipoDocumento; complemento?: string } => {
  switch (prescricao.tipo) {
    // Notificação de receita é controle especial com documento próprio por medicamento.
    case PrescricaoType.Especial:
    case PrescricaoType.Notificacao: {
      const medicamento = prescricao.medicamentos?.[0]?.medicamento
      return {
        tipo: 'controle',
        complemento: medicamento?.nome
          ? rotuloCatalogo({ ...medicamento, nome: medicamento.nome })
          : undefined,
      }
    }
    case PrescricaoType.Formula:
      return { tipo: 'formula' }
    case PrescricaoType.Exame:
      return {
        tipo: 'exame',
        complemento: prescricao.exames?.[0]?.exame?.tipo?.trim() || TIPO_EXAME_GERAL,
      }
    case PrescricaoType.Orientacoes:
      return { tipo: 'orientacao' }
    default:
      // Receita simples e receita com retenção (antimicrobianos) saem do mesmo grupo de itens.
      return { tipo: 'simples' }
  }
}

/** Tipos de prescrição que o histórico oferece para repetir; o backend separa os itens de novo ao salvar. */
export const TIPOS_REPETIVEIS = [
  PrescricaoType.Medicamento,
  PrescricaoType.Retencao,
  PrescricaoType.Especial,
  PrescricaoType.Notificacao,
  PrescricaoType.Formula,
  PrescricaoType.Exame,
]

/** Prescrições assinadas que valem para o histórico da tela (sem rascunho, atestado ou orientação). */
export const prescricoesDoHistorico = (lista: PrescricaoList[]) =>
  lista.filter(
    (prescricao) =>
      TIPOS_REPETIVEIS.includes(prescricao.tipo) &&
      prescricao.situacao === PrescricaoSituation.Assinada
  )

export type MesDoHistorico = { chave: string; rotulo: string; prescricoes: PrescricaoList[] }

/** Agrupa por mês de emissão, na ordem em que a lista chegou (mais recente primeiro). */
export const agruparPorMes = (lista: PrescricaoList[]): MesDoHistorico[] => {
  const meses: MesDoHistorico[] = []
  lista.forEach((prescricao) => {
    const chave = prescricao.data.slice(0, 7)
    let mes = meses.find((m) => m.chave === chave)
    if (!mes) {
      // `data` é LocalDateTime (com hora): `new Date` é seguro aqui.
      const rotulo = new Date(prescricao.data).toLocaleDateString('pt-BR', {
        month: 'long',
        year: 'numeric',
      })
      mes = { chave, rotulo: rotulo.charAt(0).toUpperCase() + rotulo.slice(1), prescricoes: [] }
      meses.push(mes)
    }
    mes.prescricoes.push(prescricao)
  })
  return meses
}

/** Linha de um item prescrito, para listar o que a prescrição tinha. */
export type ItemDoHistorico = { chave: string; rotulo: string; item: ItemPrescricao }

/**
 * Itens de uma prescrição salva, prontos para entrar de novo na montagem: copia posologia,
 * quantidade, observações, uso contínuo e instruções. As quantidades do LME são pedidas de novo
 * ao gerar o LME da receita nova.
 */
export const itensDaPrescricao = (prescricao: PrescricaoView): ItemDoHistorico[] => {
  const linhas: ItemDoHistorico[] = []

  prescricao.medicamentos?.forEach((prescrito) => {
    const medicamento = prescrito.medicamento
    if (!medicamento?.nome) return
    const catalogo: ItemCatalogo = {
      id: medicamento.id,
      categoria: PrescricaoType.Medicamento,
      nome: medicamento.nome,
      concentracao: medicamento.concentracao,
      apresentacao: medicamento.apresentacao,
      classe: medicamento.classe,
      tarja: medicamento.tarja,
      receituario: medicamento.receituario,
      controleEspecial: medicamento.controleEspecial,
      lme: medicamento.lme,
    }
    linhas.push({
      chave: `medicamento-${prescrito.id}`,
      rotulo: rotuloCatalogo(catalogo),
      item: criarItem(catalogo, {
        posologia: prescrito.posologia,
        quantidade: prescrito.quantidade,
        observacoes: prescrito.observacoes,
        usoContinuo: !!prescrito.usoContinuo,
      }),
    })
  })

  prescricao.formulas?.forEach((prescrita) => {
    const formula = prescrita.formula
    if (!formula?.nome) return
    const catalogo: ItemCatalogo = {
      id: formula.id,
      categoria: PrescricaoType.Formula,
      nome: formula.nome,
      composicao: formula.descricao,
    }
    linhas.push({
      chave: `formula-${prescrita.id}`,
      rotulo: catalogo.nome,
      item: criarItem(catalogo, {
        posologia: prescrita.posologia,
        quantidade: prescrita.quantidade,
        observacoes: prescrita.observacoes,
      }),
    })
  })

  prescricao.exames?.forEach((prescrito) => {
    const exame = prescrito.exame
    if (!exame?.nome) return
    const catalogo: ItemCatalogo = {
      id: exame.id,
      categoria: PrescricaoType.Exame,
      nome: exame.nome,
      tipoExame: exame.tipo,
      apac: exame.apac,
      codigo: exame.codigo,
    }
    linhas.push({
      chave: `exame-${prescrito.id}`,
      rotulo: catalogo.nome,
      item: criarItem(catalogo, {
        instrucoes: prescrito.instrucoes,
        observacoes: prescrito.observacoes,
      }),
    })
  })

  return linhas
}
